import type {
  Ability,
  AbilityFile,
  CatalogAbility,
  CatalogAbilityDetail,
  ImportResponse,
  MarketplaceAbility,
  MarketplaceAbilityDetail,
  PluginConfirmRequired,
  PluginImportReport,
} from "@bash/shared";
import { apiUrl } from "./base";
import {
  authedFetch,
  getRequiredAccessToken,
  optionalAuthedFetch,
} from "./client";
export { authedFetch, optionalAuthedFetch } from "./client";

export interface AbilityWithCounts extends Ability {
  skill_count: number;
  agent_count: number;
}

export async function listMyAbilities(): Promise<AbilityWithCounts[]> {
  const res = await authedFetch("/api/abilities/mine");
  const json = await res.json();
  return json.abilities ?? [];
}

export async function createAbility(input: {
  name: string;
  slug: string;
  description: string;
}): Promise<Ability> {
  const res = await authedFetch("/api/abilities", {
    method: "POST",
    body: JSON.stringify(input),
  });
  const json = await res.json();
  return json.ability;
}

export async function updateAbility(
  id: string,
  patch: Partial<{
    name: string;
    description: string;
    icon: string;
    color: string;
  }>
): Promise<Ability> {
  const res = await authedFetch(`/api/abilities/${id}`, {
    method: "PATCH",
    body: JSON.stringify(patch),
  });
  const json = await res.json();
  return json.ability;
}

export async function deleteAbility(id: string): Promise<void> {
  await authedFetch(`/api/abilities/${id}`, { method: "DELETE" });
}

export async function putAbilityFile(
  abilityId: string,
  path: string,
  content: string,
  contentType?: string
): Promise<AbilityFile> {
  const res = await authedFetch(`/api/abilities/${abilityId}/files`, {
    method: "PUT",
    body: JSON.stringify({ path, content, content_type: contentType }),
  });
  const json = await res.json();
  return json.file;
}

export async function deleteAbilityFile(
  abilityId: string,
  path: string
): Promise<void> {
  await authedFetch(
    `/api/abilities/${abilityId}/files?path=${encodeURIComponent(path)}`,
    { method: "DELETE" }
  );
}

export async function moveSkill(input: {
  from_ability_id: string;
  to_ability_id: string;
  skill_slug: string;
}): Promise<void> {
  await authedFetch("/api/abilities/move-skill", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

/** GET /api/abilities/skill-index — slash-command ability:skill pairs for the caller. */
export async function getSkillIndex(): Promise<
  Array<{ ability: string; skill: string }>
> {
  const res = await authedFetch("/api/abilities/skill-index");
  const json = await res.json();
  return (json.index ?? []) as Array<{ ability: string; skill: string }>;
}

// ---------------------------------------------------------------------------
// Unified catalog endpoints (Phase 2 + Phase 3 merge)
//
// `/api/abilities/catalog` returns the union of system + personal + installed
// + available rows the caller can see, each tagged with a `state`
// discriminator. The /abilities page consumes this directly. The
// `/marketplace` route is gone — both screens collapsed onto one.
// ---------------------------------------------------------------------------

export async function listCatalog(
  params: { q?: string; category?: string } = {}
): Promise<CatalogAbility[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.category) qs.set("category", params.category);
  const url = `/api/abilities/catalog${qs.toString() ? `?${qs}` : ""}`;
  // Optional auth: the catalog is public (anon → system + marketplace).
  // Send the token only when signed in so logged-out visitors can browse.
  const res = await optionalAuthedFetch(url);
  const json = await res.json();
  return json.abilities ?? [];
}

export async function getCatalogAbility(
  slug: string
): Promise<CatalogAbilityDetail> {
  // Optional auth: the detail is public for system + published marketplace
  // abilities, so anon visitors can view it (token sent only when signed in).
  const res = await optionalAuthedFetch(
    `/api/abilities/catalog/${encodeURIComponent(slug)}`
  );
  return (await res.json()) as CatalogAbilityDetail;
}

// ---------------------------------------------------------------------------
// Marketplace endpoints (Phase 3 — public catalog)
//
// Kept callable for other flows (AbilityRecommendationCard install path,
// admin-side propagation, etc.). The /marketplace UI route is gone; these
// endpoints stay because they're a different contract.
// ---------------------------------------------------------------------------

export async function listMarketplaceAbilities(
  params: {
    q?: string;
    category?: string;
    limit?: number;
    offset?: number;
  } = {}
): Promise<MarketplaceAbility[]> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.category) qs.set("category", params.category);
  if (params.limit !== undefined) qs.set("limit", String(params.limit));
  if (params.offset !== undefined) qs.set("offset", String(params.offset));
  const url = `/api/abilities/marketplace${qs.toString() ? `?${qs}` : ""}`;
  const res = await authedFetch(url);
  const json = await res.json();
  return json.abilities ?? [];
}

export async function getMarketplaceAbility(
  slug: string
): Promise<MarketplaceAbilityDetail> {
  const res = await authedFetch(
    `/api/abilities/marketplace/${encodeURIComponent(slug)}`
  );
  return (await res.json()) as MarketplaceAbilityDetail;
}

export interface InstallAbilityResult {
  ability_id: string;
  already_installed: boolean;
}

/**
 * Install a published marketplace ability for the caller.
 *
 * 201 → { ability_id, already_installed: false }
 * 409 → { ability_id, already_installed: true } (no throw — server tells us
 *       the row already exists; caller treats as success)
 * Any other non-2xx → throws via the standard error path.
 */
export async function installAbility(
  slug: string
): Promise<InstallAbilityResult> {
  const token = await getRequiredAccessToken();
  const res = await fetch(apiUrl("/api/abilities/install"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
    body: JSON.stringify({ slug }),
  });
  if (res.status === 409) {
    const body = await res.json().catch(() => ({}));
    return { ability_id: body.ability_id, already_installed: true };
  }
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${text.slice(0, 200)}`);
  }
  const json = await res.json();
  return { ability_id: json.ability_id, already_installed: false };
}

/**
 * X4: import a plugin the person confirmed (the plugin card's Install), carrying the plan's `confirm`
 * hash. A plan that changed since (the repository moved on) comes back as a new plan, not an import.
 */
export async function importConfirmedPlugin(
  source: { url: string; ref?: string; subpath?: string },
  confirm: string
): Promise<ImportResponse | PluginConfirmRequired> {
  const token = await getRequiredAccessToken();
  const res = await fetch(apiUrl("/api/items/import"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
    body: JSON.stringify({ source: { github: source }, confirm }),
  });
  const body = await res.json().catch(() => ({}));
  if (res.status === 409 && body.error === "plugin_confirm_required") return body as PluginConfirmRequired;
  if (!res.ok) throw new Error(typeof body.message === "string" ? body.message : "The plugin could not be imported. Try again.");
  return body as ImportResponse;
}

/**
 * X4: the plan of a plugin import as the person's own session sees it (a dry run), with the person's
 * confirm token for it. The plugin card fetches it on Install: the box that raised the card is never
 * handed a token.
 */
export async function fetchPluginPlan(
  source: { url: string; ref?: string; subpath?: string }
): Promise<{ plugin: PluginImportReport; confirm: string }> {
  const token = await getRequiredAccessToken();
  const res = await fetch(apiUrl("/api/items/import"), {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    credentials: "include",
    body: JSON.stringify({ source: { github: source }, dryRun: true }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok || typeof body.confirm !== "string" || !body.plugin) {
    throw new Error(typeof body.message === "string" ? body.message : "The plugin could not be read. Try again.");
  }
  return { plugin: body.plugin as PluginImportReport, confirm: body.confirm };
}

export async function uninstallAbility(slug: string): Promise<void> {
  await authedFetch(`/api/abilities/install/${encodeURIComponent(slug)}`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Phase 4 — Skill/Agent membership in user-owned abilities
//
// Lets a user compose their own ability from skills/agents they've already
// installed standalone. The server enforces invariant I1 (must be installed
// standalone before adding to an ability) and returns 409 with a clear
// install-first hint on violation. We surface the server's response body
// untouched so the UI can render the message.
// ---------------------------------------------------------------------------

export class AbilityMembershipError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly serverMessage: string
  ) {
    super(`${code}: ${serverMessage}`);
    this.name = "AbilityMembershipError";
  }
}

async function membershipMutate(
  method: "POST" | "DELETE",
  url: string
): Promise<void> {
  // Don't use authedFetch — it throws on non-2xx and we want to inspect the
  // body to surface the 409 install-first hint cleanly.
  // No Content-Type: these requests carry no body, and Fastify 400s a bodyless
  // request that announces JSON (FST_ERR_CTP_EMPTY_JSON_BODY) before the
  // handler runs — the exact header rule authedFetch enforces for the same reason.
  const token = await getRequiredAccessToken();
  const res = await fetch(apiUrl(url), {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
    },
    credentials: "include",
  });
  if (res.ok) return;
  let body: { error?: string; message?: string } = {};
  try {
    body = await res.json();
  } catch {
    /* non-JSON error body */
  }
  throw new AbilityMembershipError(
    res.status,
    body.error ?? "unknown",
    body.message ?? res.statusText
  );
}

export async function addSkillToAbility(
  abilityId: string,
  skillSlug: string
): Promise<void> {
  await membershipMutate(
    "POST",
    `/api/abilities/${abilityId}/skills/${encodeURIComponent(skillSlug)}`
  );
}

export async function removeSkillFromAbility(
  abilityId: string,
  skillSlug: string
): Promise<void> {
  await membershipMutate(
    "DELETE",
    `/api/abilities/${abilityId}/skills/${encodeURIComponent(skillSlug)}`
  );
}

export async function addAgentToAbility(
  abilityId: string,
  agentSlug: string
): Promise<void> {
  await membershipMutate(
    "POST",
    `/api/abilities/${abilityId}/agents/${encodeURIComponent(agentSlug)}`
  );
}

export async function removeAgentFromAbility(
  abilityId: string,
  agentSlug: string
): Promise<void> {
  await membershipMutate(
    "DELETE",
    `/api/abilities/${abilityId}/agents/${encodeURIComponent(agentSlug)}`
  );
}
